import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { playSfxById, SfxId } from '@/sfx';
import { PortalTooltip } from '@/ui/components';
import {
  DRAG_HOTBAR_Z,
  HUD_TEXT_MUTED,
  HUD_Z,
  UI_GHOST_BG,
  UI_PANEL_BORDER,
} from '@/ui/consts';
import { useClient, useLocale } from '@/ui/context';
import { SlotType } from '@/ui/enums';
import {
  useItemDrag,
  useItemGfxUrl,
  usePillowGfxUrl,
  useSpellIconUrls,
} from '@/ui/in-game';
import { isMobile } from '@/utils';
import { SkillIcon } from './skill-icon';
import { skillStatLine } from './skill-stat-line';

const SLOT_COUNT = 6;
const SLOT_SIZE = '3rem';
const SPELL_FRAME_W = 34;
const SPELL_FRAME_H = 32;

// ---------------------------------------------------------------------------
// Individual slot
// ---------------------------------------------------------------------------
type SlotProps = {
  index: number;
  pillow: string | null;
};

function HotBarSlot({ index, pillow }: SlotProps) {
  const client = useClient();
  const { locale } = useLocale();
  const [slots, setSlots] = useState(() => [...client.hotbarSlots]);
  const { currentDrag } = useItemDrag();
  // Hover tooltips only where there is a real pointer. On touch, the emulated
  // mouseenter fires on tap and never pairs with a mouseleave, which would
  // strand the tooltip open over a slot the player just activated.
  const [hoverCapable] = useState(() => !isMobile());

  useEffect(() => {
    return client.hotbarController.subscribe(() => {
      setSlots([...client.hotbarSlots]);
    });
  }, [client]);

  const slot = slots[index] ?? { type: SlotType.Empty, typeId: 0 };
  const isItem = slot.type === SlotType.Item;
  const isSkill = slot.type === SlotType.Skill;
  const isNewSkill = slot.type === SlotType.NewSkill;
  const skillEntry =
    isNewSkill && slot.skillId
      ? client.skillController.catalog.get(slot.skillId)
      : null;

  const itemGraphicId = isItem
    ? (client.getEifRecordById(slot.typeId)?.graphicId ?? null)
    : null;
  const spellIconId = isSkill
    ? (client.getEsfRecordById(slot.typeId)?.iconId ?? null)
    : null;

  const itemUrl = useItemGfxUrl(itemGraphicId, { shadow: true });
  const spellUrls = useSpellIconUrls(spellIconId);

  const [isSpellActive, setIsSpellActive] = useState(false);
  useEffect(() => {
    if (!isSkill) {
      setIsSpellActive(false);
      return;
    }
    const update = () =>
      setIsSpellActive(client.spellController.selectedSpellId === slot.typeId);
    client.spellController.subscribeSpellQueued(update);
    update();
    return () => {
      client.spellController.unsubscribeSpellQueued(update);
    };
  }, [client, isSkill, slot.typeId]);

  const [, forceRender] = useState(0);
  const [cooldownFrac, setCooldownFrac] = useState(0);
  const [isArmed, setIsArmed] = useState(false);
  useEffect(() => {
    let raf = 0;
    const animate = () => {
      const f = slot.skillId
        ? client.skillController.fraction(slot.skillId)
        : 0;
      setCooldownFrac(f);
      if (f > 0) raf = requestAnimationFrame(animate); // stops when ready
    };
    const onChange = () => {
      forceRender((n) => n + 1); // catalog arrival / general re-render
      setIsArmed(
        isNewSkill &&
          !!slot.skillId &&
          client.skillController.selectedSkillId === slot.skillId,
      );
      cancelAnimationFrame(raf);
      animate(); // (re)start the sweep if a cast just started
    };
    const unsub = client.skillController.subscribe(onChange);
    onChange(); // initial — covers arm state + a cooldown already active on mount (relog)
    return () => {
      cancelAnimationFrame(raf);
      unsub();
    };
  }, [client, isNewSkill, slot.skillId]);

  const pointerStart = useRef<{ x: number; y: number } | null>(null);
  const CLEAR_THRESHOLD = 30;

  const handlePointerDown = useCallback(
    (e: PointerEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (slot.type === SlotType.Empty) return;
      pointerStart.current = { x: e.clientX, y: e.clientY };
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      playSfxById(SfxId.InventoryPickup);
    },
    [slot],
  );

  const handlePointerUp = useCallback(
    (e: PointerEvent) => {
      e.preventDefault();
      const start = pointerStart.current;
      pointerStart.current = null;
      if (!start || slot.type === SlotType.Empty) return;
      const dist = Math.hypot(e.clientX - start.x, e.clientY - start.y);
      if (dist >= CLEAR_THRESHOLD) {
        client.hotbarController.clearSlot(index);
        playSfxById(SfxId.InventoryPlace);
      } else {
        client.hotbarController.activate(index);
      }
    },
    [client, slot.type, index],
  );

  const isDropTarget =
    !!currentDrag &&
    (currentDrag.source === 'inventory' ||
      currentDrag.source === 'spell' ||
      currentDrag.source === 'skill');

  const slotEl = (
    <div
      class={`relative flex items-center justify-center overflow-visible rounded border ${UI_PANEL_BORDER} ${UI_GHOST_BG} transition-colors ${
        isDropTarget
          ? 'ring-2 ring-primary/60'
          : isArmed
            ? 'ring-2 ring-amber-400'
            : ''
      }`}
      style={{
        width: SLOT_SIZE,
        height: SLOT_SIZE,
        touchAction: 'none',
      }}
      data-hotbar-slot={index}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => {
        pointerStart.current = null;
      }}
      onContextMenu={(e) => e.stopPropagation()}
    >
      {/* Key label */}
      <span
        class={`pointer-events-none absolute top-0.5 left-0.5 hidden select-none text-[0.5rem] leading-none lg:block ${HUD_TEXT_MUTED}`}
      >
        {index + 1}
      </span>

      {/* Pillow background */}
      {pillow && slot.type !== SlotType.Empty && (
        <img
          src={pillow}
          alt=''
          class='pointer-events-none absolute inset-0 h-full w-full object-contain p-0.5'
          draggable={false}
        />
      )}

      {/* Item icon */}
      {isItem && itemUrl && (
        <img
          src={itemUrl}
          alt=''
          class='pointer-events-none relative z-10 max-h-full max-w-full object-contain p-0.5'
          draggable={false}
        />
      )}

      {/* Spell icon */}
      {isSkill && spellUrls && (
        <img
          src={isSpellActive ? spellUrls.active : spellUrls.normal}
          alt=''
          class='pointer-events-none relative z-10 object-contain'
          style={{ width: `${SPELL_FRAME_W}px`, height: `${SPELL_FRAME_H}px` }}
          draggable={false}
        />
      )}

      {/* New-framework skill icon */}
      {isNewSkill && (
        <SkillIcon
          iconId={skillEntry?.iconId}
          label={skillEntry?.name ?? ''}
          tone='neutral'
          active={false}
          sizeClass='pointer-events-none relative z-10 h-8 w-8'
        />
      )}

      {/* Cooldown sweep */}
      {isNewSkill && cooldownFrac > 0 && (
        <div
          class='pointer-events-none absolute inset-0 z-20 rounded'
          style={{
            background: `conic-gradient(rgba(0,0,0,0.6) ${cooldownFrac * 360}deg, transparent 0deg)`,
          }}
        />
      )}
    </div>
  );

  // Only new-framework skills carry the catalog metadata a tooltip needs.
  if (!hoverCapable || !skillEntry) return slotEl;

  const statLine = skillStatLine(skillEntry, locale);

  // `wrapperClass` must establish a flex container: PortalTooltip's wrapper is
  // a plain <span>, and an inline wrapper would stop the fixed-size slot from
  // laying out the way it does as a direct flex child of the bar.
  return (
    <PortalTooltip
      wrapperClass='flex'
      tooltip={
        <>
          <p class='font-semibold'>{skillEntry.name}</p>
          {statLine && <p class='opacity-70'>{statLine}</p>}
          {skillEntry.description && (
            <p class='opacity-80'>{skillEntry.description}</p>
          )}
        </>
      }
    >
      {slotEl}
    </PortalTooltip>
  );
}

// ---------------------------------------------------------------------------
// HotBar
// ---------------------------------------------------------------------------
export function HotBar() {
  const pillow = usePillowGfxUrl();
  const { currentDrag } = useItemDrag();
  const barRef = useRef<HTMLDivElement>(null);
  const isDragging = currentDrag !== null;

  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const update = () => {
      const h = el.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--hotbar-h', `${h}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={barRef}
      role='presentation'
      class='flex gap-1'
      style={{ zIndex: isDragging ? DRAG_HOTBAR_Z : HUD_Z }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.stopPropagation()}
    >
      {Array.from({ length: SLOT_COUNT }, (_, i) => (
        <HotBarSlot key={i} index={i} pillow={pillow} />
      ))}
    </div>
  );
}
